<template>
  <!--
    A cover, drawn the way you SEE it: from inside the room, looking out.

    The picture is the window itself — a recess, a sash, glass — with the device's
    own photo beyond it when it has one and plain glass when it has not, and the
    cover hanging in front of that. The drawing paints no weather of its own
    (owner, 2026-10-26: *"drop the weather bg in shade window"*). Everything is
    measured in one 48-unit square so a tile, a pin and the sheet draw the same
    drawing.

    The colours are the device's own: the lamella takes its stored RAL tone and
    the sash a lighter step off it (`utils/coverColors.ts`), so the tile's blind
    is the blind the house builds. The default tone sits close to the sash so a
    device with no stored colour still reads on a dark card.

    Every kind but the awning is a window covering seen from inside the room: the
    lamella roof keeps only the two side rails (no head or sill), and the window
    kind is a roof window drawn as a Velux-style tilting sash. The awning is the
    cloth alone — a sheet wider than it is tall, rolled out of its cassette, with
    no window and no glass behind it (owner, 03.11.2026).
  -->
  <svg
    ref="svgEl"
    class="cover-anim"
    :class="[
      `cover-anim--${kind}`,
      { 'cover-anim--moving': moving, 'cover-anim--night': night, 'cover-anim--tap': interactive },
    ]"
    viewBox="0 0 48 48"
    aria-hidden="true"
    focusable="false"
    v-on="dragHandlers">
    <defs>
      <!-- Everything the cover hides is clipped to the glass, so the sash always
           reads as the opening and the fabric never spills over it. -->
      <clipPath :id="glassClipId">
        <rect :x="GLASS.x" :y="GLASS.y" :width="GLASS.size" :height="GLASS.size" rx="1.2" />
      </clipPath>
      <!-- Only the part of the glass the cover has reached: the rest is the view. -->
      <clipPath :id="bodyClipId">
        <rect :x="GLASS.x" :y="GLASS.y" :width="GLASS.size" :height="coveredHeight" />
      </clipPath>

      <!-- The glass itself: the faintest reflection, corner to corner — enough
           to read as a pane, never enough to hide the view behind it. -->
      <linearGradient :id="sheenId" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stop-color="#ffffff" stop-opacity="0.10" />
        <stop offset="28%" stop-color="#ffffff" stop-opacity="0.03" />
        <stop offset="46%" stop-color="#ffffff" stop-opacity="0" />
        <stop offset="64%" stop-color="#ffffff" stop-opacity="0.035" />
        <stop offset="100%" stop-color="#ffffff" stop-opacity="0" />
      </linearGradient>

      <!-- The reveal's shade under the head: what says the window has depth. -->
      <linearGradient :id="revealId" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stop-color="#0b1220" stop-opacity="0.20" />
        <stop offset="100%" stop-color="#0b1220" stop-opacity="0" />
      </linearGradient>

      <!-- A lamella: lit along its top edge, shaded under it. -->
      <linearGradient :id="lamellaId" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stop-color="#ffffff" stop-opacity="0.30" />
        <stop offset="34%" stop-color="#ffffff" stop-opacity="0.06" />
        <stop offset="100%" stop-color="#000000" stop-opacity="0.22" />
      </linearGradient>
    </defs>

    <!-- ── Roof window (`window`): a Velux-style top-hinged sash ─────────── -->
    <template v-if="kind === 'window'">
      <!-- The roof the window is set into, and the opening's dark reveal. -->
      <rect class="cover-anim__roof-plane" x="2.5" y="2.5" width="43" height="43" rx="2.4" />
      <rect
        class="cover-anim__roof-opening"
        :x="WINDOW.x"
        :y="WINDOW.y"
        :width="WINDOW.w"
        :height="WINDOW.h"
        rx="1.2" />
      <!-- The sash is top-hinged: closed it fills the opening, open it swings
           out and reads as a foreshortened pane — the bottom edge rises and
           narrows while the head stays on the hinge. -->
      <polygon class="cover-anim__roof-glass" :points="roofSash.points" />
      <polygon class="cover-anim__roof-sash" :points="roofSash.points" />
      <rect
        class="cover-anim__roof-rail"
        :x="roofSash.left"
        :y="roofSash.bottom - 1.5"
        :width="roofSash.width"
        height="1.5"
        rx="0.4" />
      <rect
        class="cover-anim__roof-handle"
        :x="WINDOW.x + WINDOW.w / 2 - 3"
        :y="roofSash.bottom - 1.1"
        width="6"
        height="1.8"
        rx="0.5" />
      <line class="cover-anim__roof-hinge" :x1="WINDOW.x" :y1="WINDOW.y" :x2="WINDOW.x + WINDOW.w" :y2="WINDOW.y" />
    </template>

    <!-- ── Awning: the cloth alone, wider than it is tall ────────────────────
         Nothing stands behind an awning, so nothing glassy is drawn behind it:
         the cassette at the head and the flat sheet it rolls out, which is the
         whole picture (owner, 03.11.2026: *"should be rectangular, wider than
         taller… should not have a 'glass' like background. just make it gray
         without glass effect"*). -->
    <template v-else-if="kind === 'awning'">
      <rect
        class="cover-anim__cassette"
        :x="AWNING.x"
        :y="AWNING.y"
        :width="AWNING.w"
        :height="AWNING_CASSETTE"
        rx="0.8" />
      <rect
        class="cover-anim__fabric"
        :x="AWNING.x"
        :y="AWNING.y + AWNING_CASSETTE"
        :width="AWNING.w"
        :height="awningDrop" />
      <rect
        v-if="awningDrop > AWNING_BAR"
        class="cover-anim__awning-bar"
        :x="AWNING.x"
        :y="AWNING.y + AWNING_CASSETTE + awningDrop - AWNING_BAR"
        :width="AWNING.w"
        :height="AWNING_BAR" />
    </template>

    <!-- ── Window coverings, seen from inside the room ───────────────────── -->
    <template v-else>
      <!-- The opening: the device's own photo when it has one, and plain glass
           when it does not. The drawing paints no weather of its own (owner,
           2026-10-26: "drop the weather bg in shade window") — a picture or a
           pane, never a forecast. -->
      <g :clip-path="`url(#${glassClipId})`">
        <rect class="cover-anim__pane" :x="GLASS.x" :y="GLASS.y" :width="GLASS.size" :height="GLASS.size" />
        <image
          v-if="backgroundUrl"
          :href="backgroundUrl"
          :x="GLASS.x"
          :y="GLASS.y"
          :width="GLASS.size"
          :height="GLASS.size"
          preserveAspectRatio="xMidYMid slice"
          class="cover-anim__scene" />
      </g>

      <!-- The cover, hanging in front of the view and behind the glass. -->
      <g :clip-path="`url(#${glassClipId})`">
        <g :clip-path="`url(#${bodyClipId})`">
          <!-- Roller / zip screen: one sheet, with a seam where it meets the
               roll and the hem stitch that gives the cloth a scale at tile size
               (owner, 27.10.2026: "the dashes are too small and not visible"). -->
          <template v-if="kind === 'roller' || kind === 'screen'">
            <rect class="cover-anim__fabric" :x="GLASS.x" :y="GLASS.y" :width="GLASS.size" :height="coveredHeight" />

            <rect
              class="cover-anim__fabric-edge"
              :x="GLASS.x"
              :y="GLASS.y + Math.max(0, coveredHeight - 0.5)"
              :width="GLASS.size"
              height="0.5" />
          </template>

          <!-- Roller shutter: slats, and the HOLES between them. A shutter's
               perforations close up over the first quarter of its travel (the
               slats overlap as they seat), so "hole" is a property of the
               POSITION, not a decoration — at 25 % the shutter is solid. -->
          <template v-else-if="kind === 'shutter'">
            <rect
              v-for="slat in shutterSlats"
              :key="slat.i"
              class="cover-anim__slat"
              :x="GLASS.x"
              :y="GLASS.y + slat.y"
              :width="GLASS.size"
              :height="slat.height" />
            <rect
              class="cover-anim__fabric-edge"
              :x="GLASS.x"
              :y="GLASS.y + Math.max(0, coveredHeight - 0.5)"
              :width="GLASS.size"
              height="0.5" />
          </template>

          <!-- Venetian blind and lamella roof: the same lamellas, the gap
               between them the angle the tilt opens. The lamella roof is the
               blind in a frame with only its two side rails. -->
          <template v-else-if="kind === 'venetian' || kind === 'lamella'">
            <g
              v-for="(y, i) in slatYs"
              :key="i"
              class="cover-anim__lamella"
              :style="{ transform: `translateY(${GLASS.y + y}px) scaleY(${slatScale})` }">
              <rect class="cover-anim__slat" :x="GLASS.x" y="0" :width="GLASS.size" :height="SLAT_HEIGHT" rx="0.4" />
              <rect
                :fill="`url(#${lamellaId})`"
                :x="GLASS.x"
                y="0"
                :width="GLASS.size"
                :height="SLAT_HEIGHT"
                rx="0.4" />
            </g>
          </template>
        </g>

        <!-- The glass, over everything behind it: the reflection and the
             reveal's shade are what turn a flat drawing into a window. -->
        <rect
          :x="GLASS.x"
          :y="GLASS.y"
          :width="GLASS.size"
          :height="GLASS.size"
          :fill="`url(#${sheenId})`"
          class="cover-anim__sheen" />
        <rect :x="GLASS.x" :y="GLASS.y" :width="GLASS.size" :height="GLASS.size * 0.12" :fill="`url(#${revealId})`" />
      </g>

      <!-- The frame: a lamella roof's is only its two side rails, everything
           else keeps the full sash. -->
      <template v-if="kind === 'lamella'">
        <rect class="cover-anim__side-rail" x="8" y="8" width="1.4" height="32" rx="0.7" />
        <rect class="cover-anim__side-rail" x="38.6" y="8" width="1.4" height="32" rx="0.7" />
      </template>
      <template v-else>
        <rect
          class="cover-anim__sash"
          :x="SASH.x"
          :y="SASH.y"
          :width="SASH.size"
          :height="SASH.size"
          rx="1.8"
          :stroke="colors.frame" />
        <rect
          class="cover-anim__sash-edge"
          :x="GLASS.x"
          :y="GLASS.y"
          :width="GLASS.size"
          :height="GLASS.size"
          rx="1.2" />
      </template>
    </template>
  </svg>
</template>

<script lang="ts">
/** Module scope: ids must be unique per instance, or the first clip wins for every tile. */
let instanceSeq = 0;
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type { CoverKind } from '../deviceWidgetTypes';

const props = withDefaults(
  defineProps<{
    kind?: CoverKind;
    /** 0 = fully open, 100 = fully closed (the hub's cover convention). */
    position?: number;
    /** Slat tilt for venetian blinds and lamella roofs: 0 = slats open
        (widest gap), 100 = slats shut (the hub's cover convention). */
    tilt?: number | null;
    /** Cover is travelling — the panel breathes so it reads as moving. */
    moving?: boolean;
    /** The lamella / fabric tone, taken from the device's own RAL code. */
    materialColor?: string;
    /** The sash's tone — a lighter step off the same code. */
    frameColor?: string;
    /** The device's own picture, shown as the view beyond the glass. With no
        picture the opening is plain glass — the drawing paints no weather. */
    backgroundUrl?: string | null;
    /** After sunset the picture is shown greyed out, as the eye sees it. */
    night?: boolean;
    /** The drawing is a control: dragging it picks a position (left button) or
        a slat tilt (right button), and the release sends it. Off by default — a
        picture elsewhere (the preview matrix, a pin) stays a picture. */
    interactive?: boolean;
  }>(),
  {
    kind: 'roller',
    position: 0,
    tilt: null,
    moving: false,
    // Close to the sash tone: a shade with no stored RAL must not turn into a
    // black rectangle on a dark card (owner, 27.10.2026).
    materialColor: '#a3adbb',
    frameColor: '#c6ced9',
    backgroundUrl: null,
    night: false,
    interactive: false,
  },
);

const emit = defineEmits<{
  /** A drag released (or left): the position the drawing was dragged to. */
  target: [position: number];
  /** A right-drag released (or left): the slat tilt it was dragged to. */
  tiltTarget: [tilt: number];
}>();

/** The opening's geometry, in viewBox units — one square, centred. */
const GLASS = { x: 9, y: 9, size: 30 };
/** The sash's band, centred on this outline, so it frames the glass exactly. */
const SASH = { x: 8, y: 8, size: 32 };

/** An awning hangs on no window: the cloth is the whole drawing, and it is made
    wider than it is tall so a tile of awnings never reads as another pane. */
const AWNING = { x: 4, y: 10.5, w: 40, h: 27 };
/** The cassette at its head, and the front bar weighting its leading edge. */
const AWNING_CASSETTE = 3.4;
const AWNING_BAR = 1.2;

/** Slat pitch/height of the venetian blind: six lamellas fill the glass. */
const SLAT_PITCH = 30 / 6;
const SLAT_HEIGHT = SLAT_PITCH - 0.9;

/** A shutter is solid from a quarter of its travel: the holes seat first. */
const SHUTTER_HOLE_RANGE = 0.25;
/** How many slats fill the glass, and how much of one pitch its hole can take. */
const SHUTTER_SLATS = 18;
const SHUTTER_HOLE_RATIO = 0.45;

/** The roof window's opening, in viewBox units — portrait, set in a roof. */
const WINDOW = { x: 12, y: 7.5, w: 24, h: 33 };

const clampPct = (value: number) => Math.min(100, Math.max(0, value));

// ── The drawing as a control (drag to set) ──────────────────────────────────
//
// A left drag down the glass picks the position, a right drag the slat tilt —
// the same axis for both, because dragging down closes either one. Nothing is
// sent while the finger is down: the drawing follows it (below), and the
// release — or a drag that runs off the drawing — sends one target, so a drag
// across the glass is one command instead of forty.
const svgEl = ref<SVGSVGElement | null>(null);

/** The drag in flight, and the value it has reached. */
let dragKind: 'position' | 'tilt' | null = null;
let dragPointer: number | null = null;
let dragValue = 0;

/** A released value, drawn until the hub reports it back (or gives up on it). */
const preview = ref<{ kind: 'position' | 'tilt'; value: number } | null>(null);
/** How long a released value stands if the hub never echoes it. */
const PREVIEW_HOLD_MS = 2500;
let previewTimer: number | null = null;

const livePosition = computed(() => (preview.value?.kind === 'position' ? preview.value.value : props.position));
const liveTilt = computed(() => (preview.value?.kind === 'tilt' ? preview.value.value : props.tilt));

/** The hub echoing the released value ends the preview; the device that never
    answers falls back on its own reading when the hold runs out. */
watch(
  () => [props.position, props.tilt] as const,
  ([position, tilt]) => {
    const held = preview.value;
    if (!held || dragKind !== null) return;
    if ((held.kind === 'position' ? position : tilt) === held.value) preview.value = null;
  },
);

/**
 * The span a position is read down: the glass for every window kind, and the
 * awning's own sheet — the picture a finger drags is the one that answers.
 */
const travelSpan = computed(() =>
  props.kind === 'awning'
    ? { x: AWNING.x, y: AWNING.y + AWNING_CASSETTE, size: AWNING.h - AWNING_CASSETTE }
    : { x: GLASS.x, y: GLASS.y, size: GLASS.size },
);

/**
 * Where a screen point sits in the drawing: 0 % at its head, 100 % at its sill —
 * the hub's cover convention, and the axis both registers travel on. Read
 * through the SVG's own matrix, so a tile, a pin and the sheet all answer with
 * the same percentage for the same point of the drawing.
 */
function positionAt(clientY: number): number | null {
  const svg = svgEl.value;
  const matrix = svg?.getScreenCTM();
  if (!svg || !matrix) return null;
  const span = travelSpan.value;
  const point = svg.createSVGPoint();
  point.x = span.x;
  point.y = clientY;
  const local = point.matrixTransform(matrix.inverse());
  return Math.round(clampPct(((local.y - span.y) / span.size) * 100));
}

function startDrag(e: PointerEvent): void {
  if (!props.interactive || e.isPrimary === false) return;
  // Left button (or a touch/pen) drives the position, the right button the tilt
  // of the kinds that have slats at all.
  const kind = e.button === 2 ? 'tilt' : e.button === 0 ? 'position' : null;
  if (kind === null || (kind === 'tilt' && props.tilt === null)) return;
  const pct = positionAt(e.clientY);
  if (pct === null) return;
  // The drawing owns the press: the card behind it must not read a tap of its
  // own off the same finger, and a touch drag must not scroll the page.
  e.preventDefault();
  e.stopPropagation();
  try {
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
  } catch {
    /* no capture to lose */
  }
  dragKind = kind;
  dragPointer = e.pointerId;
  dragValue = pct;
  preview.value = { kind, value: pct };
}

function moveDrag(e: PointerEvent): void {
  if (dragKind === null || e.pointerId !== dragPointer) return;
  const pct = positionAt(e.clientY);
  if (pct === null) return;
  e.stopPropagation();
  dragValue = pct;
  preview.value = { kind: dragKind, value: pct };
}

/** Release, a drag that ran off the drawing, and a cancelled touch all end the
    same way: one target, sent once, held on screen until the hub answers. */
function endDrag(e: PointerEvent): void {
  if (dragKind === null || e.pointerId !== dragPointer) return;
  e.stopPropagation();
  const kind = dragKind;
  const value = dragValue;
  dragKind = null;
  dragPointer = null;
  if (previewTimer !== null) window.clearTimeout(previewTimer);
  previewTimer = window.setTimeout(() => {
    previewTimer = null;
    preview.value = null;
  }, PREVIEW_HOLD_MS);
  if (kind === 'position') emit('target', value);
  else emit('tiltTarget', value);
}

/** The click the browser still synthesises after the press we consumed. */
function swallowClick(e: MouseEvent): void {
  if (props.interactive) e.stopPropagation();
}

/** A right drag is the tilt, so its menu never opens over the drawing. */
function swallowMenu(e: MouseEvent): void {
  if (!props.interactive) return;
  e.preventDefault();
  e.stopPropagation();
}

/** Bound only when the drawing is a control — otherwise it is just a picture. */
const dragHandlers = computed(() => {
  if (!props.interactive) return {};
  return {
    pointerdown: startDrag,
    pointermove: moveDrag,
    pointerup: endDrag,
    pointercancel: endDrag,
    pointerleave: endDrag,
    lostpointercapture: endDrag,
    click: swallowClick,
    contextmenu: swallowMenu,
  };
});

const closedFrac = computed(() => clampPct(livePosition.value) / 100);

/** How far down the glass the cover has reached, in viewBox units. */
const coveredHeight = computed(() => GLASS.size * closedFrac.value);

/** How far the awning's sheet has rolled out of its cassette, in viewBox units. */
const awningDrop = computed(() => (AWNING.h - AWNING_CASSETTE) * closedFrac.value);

/** 0 = slats open, 1 = slats shut. No tilt reported → a neutral angle. */
const shutFrac = computed(() => (liveTilt.value === null ? 0.55 : clampPct(liveTilt.value) / 100));

/** A lamella turned open shrinks onto its edge; shut, it overlaps its neighbour. */
const slatScale = computed(() => 0.4 + 0.82 * shutFrac.value);

const slatYs = Array.from({ length: 6 }, (_, i) => +(i * SLAT_PITCH).toFixed(2));

/**
 * The shutter's own image: as it comes down, the holes between the slats close
 * up, and by a quarter of the travel they are gone. So the gap is a function of
 * the POSITION — the owner's *"we must take into account the shutter holes to
 * close up it takes 25% of range"* — and every slat is drawn at that same gap,
 * so the whole surface closes together.
 */
const shutterHoleFrac = computed(() => Math.max(0, 1 - closedFrac.value / SHUTTER_HOLE_RANGE));
const shutterPitch = GLASS.size / SHUTTER_SLATS;
const shutterSlats = computed(() => {
  const gap = shutterPitch * SHUTTER_HOLE_RATIO * shutterHoleFrac.value;
  const height = Math.max(0.16, shutterPitch - gap);
  return Array.from({ length: SHUTTER_SLATS }, (_, i) => ({
    i,
    y: +(i * shutterPitch).toFixed(2),
    height: +height.toFixed(2),
  }));
});

/**
 * The roof window's tilting sash. Top-hinged: closed it fills the opening, and
 * as it opens its bottom edge rises and narrows while the head stays on the
 * hinge — the foreshortened trapezoid a Velux reads as from below.
 */
const roofSash = computed(() => {
  const open = 1 - closedFrac.value;
  const bottom = WINDOW.y + WINDOW.h * (1 - 0.42 * open);
  const inset = (WINDOW.w / 2) * 0.22 * open;
  const left = WINDOW.x + inset;
  const right = WINDOW.x + WINDOW.w - inset;
  return {
    bottom,
    left,
    width: right - left,
    points: `${WINDOW.x},${WINDOW.y} ${WINDOW.x + WINDOW.w},${WINDOW.y} ${right},${bottom} ${left},${bottom}`,
  };
});

/** The two tones, with the material's own step for the sash when none is given. */
const colors = computed(() => ({ material: props.materialColor, frame: props.frameColor }));

/** Ids must be unique per instance, otherwise the first clip wins for every tile. */
const uid = `cover-anim-${++instanceSeq}`;
const glassClipId = `${uid}-glass`;
const bodyClipId = `${uid}-body`;
const sheenId = `${uid}-sheen`;
const revealId = `${uid}-reveal`;
const lamellaId = `${uid}-lamella`;
</script>

<style scoped>
.cover-anim {
  display: block;
  width: 100%;
  height: 100%;
}

/* The drawing is a control when the host makes it one: the drag runs up and
   down it, so the cursor says so, and the gesture is the drawing's own — a
   touch drag across the glass must not scroll the page under it. */
.cover-anim--tap {
  cursor: ns-resize;
  touch-action: none;
}

/* The device's own photo, seen through the glass: full colour by day, and the
   greyed-out view the eye has after sunset. */
.cover-anim__scene {
  transition: filter 0.4s ease;
}

.cover-anim--night .cover-anim__scene {
  filter: grayscale(1) brightness(0.62) contrast(1.05);
}

/* The lamella / fabric takes the device's own RAL tone. */
.cover-anim__fabric,
.cover-anim__slat {
  fill: v-bind('colors.material');
}

/* A roof window's pane is glass: the view stays visible through it, and only
   its own sash and rail carry the device's colour. */
.cover-anim__pane {
  /* Glass, with nothing behind it: the weather gradient that used to be painted
     here is gone (owner, 2026-10-26), so the pane itself carries the tone a
     blind is read against — soft enough to stay a window, never a wall. */
  fill: rgba(158, 186, 214, 0.22);
  stroke-width: 0.5;
}

/* A lamella roof's frame: only the two side rails — no head, no sill. */
.cover-anim__side-rail {
  fill: v-bind('colors.frame');
}

/* An awning's cassette: the narrow box at the head the cloth rolls out of. */
.cover-anim__cassette {
  fill: v-bind('colors.frame');
  stroke: rgba(0, 0, 0, 0.25);
  stroke-width: 0.4;
}

/* The awning's front bar: the leading edge of the cloth, in the same metal as
   the cassette, so the sheet reads between two hard lines instead of fading
   out (the awning has no sill or sash to end it). */
.cover-anim__awning-bar {
  fill: v-bind('colors.frame');
}

/* The leading edge: a hairline, never the thick bar it used to be — at tile size
   a 3.6-unit rail read as a black band under the blind. */
.cover-anim__fabric-edge {
  fill: rgba(0, 0, 0, 0.28);
}

/* ── The roof window: a tilting sash in a roof ───────────────────────────────
   The plane is the setting, not the window — a soft slate wash that reads in
   both themes; the sash and the handle carry the device's own tones. */
.cover-anim__roof-plane {
  fill: rgba(148, 163, 184, 0.16);
  stroke: rgba(148, 163, 184, 0.32);
  stroke-width: 0.6;
}

.cover-anim__roof-opening {
  fill: rgba(2, 6, 23, 0.55);
  stroke: rgba(255, 255, 255, 0.1);
  stroke-width: 0.4;
}

.cover-anim__roof-glass {
  fill: rgba(158, 186, 214, 0.32);
}

.cover-anim__roof-sash {
  fill: none;
  stroke: v-bind('colors.frame');
  stroke-width: 1.6;
  stroke-linejoin: round;
}

.cover-anim__roof-rail {
  fill: v-bind('colors.material');
}

.cover-anim__roof-handle {
  fill: v-bind('colors.frame');
}

.cover-anim__roof-hinge {
  stroke: v-bind('colors.frame');
  stroke-width: 1.4;
  stroke-linecap: round;
}

/* The sash: a band around the opening, with a bright inner edge — the moulding
   that makes the drawing read as a window rather than a rectangle. */
.cover-anim__sash {
  fill: none;
  stroke-width: 1.6;
}

.cover-anim__sash-edge {
  fill: none;
  stroke: rgba(255, 255, 255, 0.22);
  stroke-width: 0.4;
}

/* Decoration only — never a hit target (the circle owns the tap) */
.cover-anim__sheen {
  pointer-events: none;
}

/* Lamellas turn about their own centre line. */
.cover-anim__lamella {
  transform-box: fill-box;
  transform-origin: center;
  transition: transform 0.5s ease;
}

/* Travelling: a sheen sweeps the glass and the cover breathes */
.cover-anim--moving .cover-anim__sheen {
  animation: cover-anim-sheen 1.8s ease-in-out infinite;
}

.cover-anim--moving .cover-anim__fabric,
.cover-anim--moving .cover-anim__slat {
  animation: cover-anim-breathe 1.8s ease-in-out infinite;
}

@keyframes cover-anim-sheen {
  0%,
  100% {
    opacity: 0.55;
  }
  50% {
    opacity: 1;
  }
}

@keyframes cover-anim-breathe {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.86;
  }
}

@media (prefers-reduced-motion: reduce) {
  .cover-anim__lamella,
  .cover-anim__scene {
    transition: none;
  }
  .cover-anim--moving .cover-anim__sheen,
  .cover-anim--moving .cover-anim__fabric,
  .cover-anim--moving .cover-anim__slat {
    animation: none;
  }
}
</style>
